// Lightweight, dependency-free i18n. Three locales, one dictionary object.
// The reconciliation headline is generated here (not taken from the core, which
// emits Russian) so the whole visible UI follows the selected language.

export type Lang = "sr" | "en" | "ru";

export const LANGS: { code: Lang; label: string }[] = [
  { code: "sr", label: "SRB" },
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
];

const LOCALE: Record<Lang, string> = {
  sr: "sr-RS",
  en: "en-US",
  ru: "ru-RU",
};

/** Money formatting follows the selected UI language. */
export function fmtMoney(n: number, lang: Lang): string {
  return new Intl.NumberFormat(LOCALE[lang], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** A rich fragment with an emphasised / linked middle part. */
interface RichParts {
  a: string;
  mid: string;
  c: string;
}

export interface Strings {
  localBadge: string;
  heroTitle: string;
  heroLead: RichParts; // `mid` is bolded
  dropTitle: string;
  dropSub: string;
  dropNote: string;
  processing: string;
  errNotPdf: string;
  errNoText: string;
  errGeneric: string;
  errUnknownBank: string;
  reconOk: string;
  reconMismatch: (diff: string) => string;
  reconNoBalance: string;
  reconDetail: (p: {
    opening: string;
    computed: string;
    closing: string;
    diff: string;
    ok: boolean;
  }) => string;
  bank: string;
  account: string;
  period: string;
  currency: string;
  txCount: (n: number) => string;
  colDate: string;
  colDesc: string;
  colAmount: string;
  colBalance: string;
  warningsTitle: (n: number) => string;
  whyTitle: string;
  whyBody: RichParts; // `mid` is emphasised (em)
  sendSample: RichParts; // `mid` is the mailto link text
  footer: string;
  pro: ProStrings;
}

export interface ProStrings {
  btn: string;
  active: string;
  title: string;
  lede: string;
  benefits: string[];
  cta: string;
  soon: string;
  haveKey: string;
  keyPlaceholder: string;
  unlock: string;
  unlockError: string;
  close: string;
  limit: string;
}

export const STRINGS: Record<Lang, Strings> = {
  sr: {
    localBadge: "Lokalno u pregledaču",
    heroTitle: "PDF izvod banke → Excel / CSV",
    heroLead: {
      a: "Fajl ",
      mid: "ne napušta uređaj",
      c: ". Provera balansa garantuje da nijedna transakcija nije izgubljena.",
    },
    dropTitle: "Prevucite PDF izvod ovde",
    dropSub: "ili kliknite da izaberete fajl",
    dropNote: "Sve se obrađuje lokalno. Ništa se ne šalje na server.",
    processing: "Obrada… (lokalno, fajl ostaje na uređaju)",
    errNotPdf: "Podržani su samo PDF fajlovi (skenovi i fotografije zasad ne).",
    errNoText:
      "U PDF-u nije pronađen tekst. Verovatno je sken/fotografija — zasad nije podržano.",
    errGeneric: "Greška pri obradi PDF-a. Pogledajte konzolu za detalje.",
    errUnknownBank:
      "Banka nije prepoznata. Pošaljite primer izvoda — dodaćemo šablon vaše banke.",
    reconOk: "✓ Balans se poklapa: početno + zbir transakcija = krajnje stanje.",
    reconMismatch: (d) =>
      `✗ Razlika ${d}: možda su transakcije izostavljene ili pogrešno pročitane.`,
    reconNoBalance: "Balans nije pronađen u izvodu.",
    reconDetail: ({ opening, computed, closing, diff, ok }) =>
      `Početno ${opening} + Σ transakcija = ${computed} · na izvodu ${closing}` +
      (ok ? "" : ` · razlika ${diff}`),
    bank: "Banka",
    account: "Račun",
    period: "Period",
    currency: "Valuta",
    txCount: (n) => `Transakcija: ${n}`,
    colDate: "Datum",
    colDesc: "Opis",
    colAmount: "Iznos",
    colBalance: "Stanje",
    warningsTitle: (n) => `Napomene parsera (${n})`,
    whyTitle: "Zašto je ovo tačnije od AI alata",
    whyBody: {
      a: "Koristimo pravila po banci (ne „pogađanje“ modelom) i proveru: ",
      mid: "početno stanje + zbir transakcija = krajnje stanje",
      c: ". Ako se poklopi, parsiranje je zagarantovano potpuno. Vaš fajl se obrađuje u pregledaču pomoću pdf.js — privatnost je tehnička činjenica, ne obećanje.",
    },
    sendSample: {
      a: "Vaše banke nema na listi? ",
      mid: "Pošaljite primer izvoda",
      c: " i dodaćemo šablon.",
    },
    footer: "BalkanStatements · MVP · Read-only konverzija. Ne uređujemo izvode.",
    pro: {
      btn: "Pro",
      active: "Pro aktivan ✓",
      title: "BalkanStatements Pro",
      lede: "Besplatni konverter ostaje besplatan. Pro dodaje:",
      benefits: [
        "Neograničene konverzije (besplatno: 1 dnevno)",
        "Grupno učitavanje više PDF-ova odjednom",
        "Excel sa formatiranjem i sumama",
        "Sažetak prihoda i rashoda po kategorijama",
        "Potvrda o prihodu na SR/EN za boravak ili kredit",
      ],
      cta: "Postanite Pro",
      soon: "Uskoro. Kliknite da nam javite da ste zainteresovani.",
      haveKey: "Već imate ključ?",
      keyPlaceholder: "Licencni ključ",
      unlock: "Otključaj",
      unlockError: "Ključ nije prepoznat.",
      close: "Zatvori",
      limit: "Dostigli ste besplatni dnevni limit. Postanite Pro za neograničeno.",
    },
  },
  en: {
    localBadge: "Runs in your browser",
    heroTitle: "PDF bank statement → Excel / CSV",
    heroLead: {
      a: "The file ",
      mid: "never leaves your device",
      c: ". A balance check guarantees no transaction is lost.",
    },
    dropTitle: "Drag your PDF statement here",
    dropSub: "or click to choose a file",
    dropNote: "Everything is processed locally. Nothing is sent to a server.",
    processing: "Processing… (locally, the file stays on your device)",
    errNotPdf: "Only PDF files are supported (scans and photos not yet).",
    errNoText:
      "No text found in the PDF. It is probably a scan/photo — not supported yet.",
    errGeneric: "Error while processing the PDF. See the console for details.",
    errUnknownBank:
      "Bank not recognised. Send a sample statement — we'll add a template for your bank.",
    reconOk: "✓ Balanced: opening + sum of transactions = closing balance.",
    reconMismatch: (d) =>
      `✗ Difference ${d}: some transactions may be missing or misread.`,
    reconNoBalance: "Balance not found in the statement.",
    reconDetail: ({ opening, computed, closing, diff, ok }) =>
      `Opening ${opening} + Σ transactions = ${computed} · on statement ${closing}` +
      (ok ? "" : ` · difference ${diff}`),
    bank: "Bank",
    account: "Account",
    period: "Period",
    currency: "Currency",
    txCount: (n) => `Transactions: ${n}`,
    colDate: "Date",
    colDesc: "Description",
    colAmount: "Amount",
    colBalance: "Balance",
    warningsTitle: (n) => `Parser notes (${n})`,
    whyTitle: "Why this is more accurate than AI tools",
    whyBody: {
      a: "We use per-bank rules (not model „guessing“) and a check: ",
      mid: "opening balance + sum of transactions = closing balance",
      c: ". If it matches, the parse is provably complete. Your file is processed in the browser with pdf.js — privacy is a technical fact, not a promise.",
    },
    sendSample: {
      a: "Your bank isn't listed? ",
      mid: "Send a sample statement",
      c: " and we'll add a template.",
    },
    footer:
      "BalkanStatements · MVP · Read-only conversion. We never edit statements.",
    pro: {
      btn: "Pro",
      active: "Pro active ✓",
      title: "BalkanStatements Pro",
      lede: "The free converter stays free. Pro adds:",
      benefits: [
        "Unlimited conversions (free: 1 per day)",
        "Batch-convert multiple PDFs at once",
        "Formatted Excel with totals",
        "Income & expense summary by category",
        "Income proof in SR/EN for residence permit or mortgage",
      ],
      cta: "Get Pro",
      soon: "Coming soon. Click to tell us you're interested.",
      haveKey: "Already have a key?",
      keyPlaceholder: "License key",
      unlock: "Unlock",
      unlockError: "Key not recognised.",
      close: "Close",
      limit: "You've reached the free daily limit. Get Pro for unlimited.",
    },
  },
  ru: {
    localBadge: "Локально в браузере",
    heroTitle: "PDF-выписка банка → Excel / CSV",
    heroLead: {
      a: "Файл ",
      mid: "не покидает устройство",
      c: ". Проверка баланса гарантирует, что ни одна транзакция не потеряна.",
    },
    dropTitle: "Перетащите PDF-выписку сюда",
    dropSub: "или нажмите, чтобы выбрать файл",
    dropNote: "Всё обрабатывается локально. Ничего не отправляется на сервер.",
    processing: "Обработка… (локально, файл остаётся на устройстве)",
    errNotPdf: "Поддерживаются только PDF (сканы и фото пока нет).",
    errNoText:
      "В PDF не найден текст. Вероятно, это скан/фото — пока не поддерживается.",
    errGeneric: "Ошибка при обработке PDF. Подробности в консоли.",
    errUnknownBank:
      "Банк не распознан. Отправьте пример выписки — добавим шаблон вашего банка.",
    reconOk: "✓ Баланс сошёлся: начальный + сумма транзакций = конечный.",
    reconMismatch: (d) =>
      `✗ Расхождение ${d}: возможно, транзакции пропущены или неверно распознаны.`,
    reconNoBalance: "Баланс не найден в выписке.",
    reconDetail: ({ opening, computed, closing, diff, ok }) =>
      `Начальный ${opening} + Σ транзакций = ${computed} · в выписке ${closing}` +
      (ok ? "" : ` · расхождение ${diff}`),
    bank: "Банк",
    account: "Счёт",
    period: "Период",
    currency: "Валюта",
    txCount: (n) => `Транзакций: ${n}`,
    colDate: "Дата",
    colDesc: "Описание",
    colAmount: "Сумма",
    colBalance: "Остаток",
    warningsTitle: (n) => `Заметки парсера (${n})`,
    whyTitle: "Почему это точнее AI-инструментов",
    whyBody: {
      a: "Мы используем правила по каждому банку (а не «угадывание» моделью) и проверку: ",
      mid: "начальный баланс + сумма транзакций = конечный баланс",
      c: ". Если сходится — разбор гарантированно полный. Файл обрабатывается в браузере через pdf.js: приватность — технический факт, а не обещание.",
    },
    sendSample: {
      a: "Вашего банка нет в списке? ",
      mid: "Отправьте пример выписки",
      c: " — добавим шаблон.",
    },
    footer:
      "BalkanStatements · MVP · Только чтение. Мы не редактируем выписки.",
    pro: {
      btn: "Pro",
      active: "Pro активен ✓",
      title: "BalkanStatements Pro",
      lede: "Бесплатный конвертер остаётся бесплатным. Pro добавляет:",
      benefits: [
        "Безлимитные конвертации (бесплатно: 1 в день)",
        "Пакетная загрузка нескольких PDF сразу",
        "Excel с форматированием и итогами",
        "Сводка доходов и расходов по категориям",
        "Справка о доходе на SR/EN для ВНЖ или ипотеки",
      ],
      cta: "Оформить Pro",
      soon: "Скоро. Нажмите, чтобы сообщить, что вам интересно.",
      haveKey: "Уже есть ключ?",
      keyPlaceholder: "Лицензионный ключ",
      unlock: "Разблокировать",
      unlockError: "Ключ не распознан.",
      close: "Закрыть",
      limit: "Достигнут бесплатный дневной лимит. Оформите Pro для безлимита.",
    },
  },
};

/** Initial language: ?lang= query (from a landing page) → stored → browser → EN. */
export function initialLang(): Lang {
  try {
    const q = new URLSearchParams(location.search).get("lang");
    if (q === "sr" || q === "en" || q === "ru") return q;
  } catch {
    /* location may be unavailable */
  }
  try {
    const saved = localStorage.getItem("lang");
    if (saved === "sr" || saved === "en" || saved === "ru") return saved;
  } catch {
    /* localStorage may be unavailable */
  }
  const nav = (navigator.language || "").toLowerCase();
  if (nav.startsWith("ru")) return "ru";
  if (nav.startsWith("sr") || nav.startsWith("hr") || nav.startsWith("bs")) return "sr";
  return "en";
}
